/* eslint-disable prettier/prettier */
import { useRef, useEffect, useState } from 'react';
import { Eye, Pencil, Trash2, MoreVertical } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';
import { formatSize } from '../../utils/formatSize';

interface FileRowProps {
  file: {
    id: string;
    editedName: string;
    uploadedAt: string;
    size: string;
    fileName: string;
  };
  index: number;
  isSelected?: boolean;
  onSelect?: (checked: boolean) => void;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const FileRow = ({
  file,
  index,
  isSelected = false,
  onSelect,
  onView,
  onEdit,
  onDelete,
}: FileRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      onDoubleClick={onView}
      className="cursor-pointer sm:w-[90%] relative flex items-center px-4 py-3 bg-white border-b border-x hover:bg-light"
    >
      {/* Selection Checkbox */}
      <div className="w-[5%] flex justify-start items-center pr-2">
        {onSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            className="accent-primary"
          />
        )}
      </div>

      {/* File Name */}
      <div className="w-[55%] text-primary font-medium flex items-center gap-2">
        <p
          className="mr-1 text-sm leading-snug break-words sm:text-base line-clamp-1 sm:mr-0"
          title={file.editedName || file.fileName}
        >
          {file.editedName || file.fileName}
        </p>
      </div>

      {/* Date */}
      <div className="w-[20%] text-sm text-muted-foreground mr-1 sm:mr-0">
        {formatDate(file.uploadedAt)}
      </div>

      {/* Size */}
      <div className="w-[15%] text-sm text-muted-foreground">
        {formatSize(file.size)}
      </div>

      {/* Actions */}
      <div className="w-[5%] flex justify-end relative">
        <button ref={buttonRef} onClick={() => setIsOpen(!isOpen)}>
          <MoreVertical className="w-5 h-5 text-muted-foreground" />
        </button>
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute right-0 z-10 mt-2 bg-white border rounded shadow-md w-28"
          >
            <button
              className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-highlight"
              onClick={onView}
            >
              <Eye className="w-4 h-4" /> View
            </button>
            <button
              className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-highlight"
              onClick={onEdit}
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
            <button
              className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-red-600 hover:bg-highlight"
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
